import { DescriptionOutlined } from "@mui/icons-material";
import { useRouter } from "next/router";
import React from "react";
import { CompensationReview } from "~/components/ui/core/icons/CompensationReviewIcon";
import { Link } from "~/components/ui/core/Link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "~/components/ui/shadcn/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  useSidebar,
} from "~/components/ui/shadcn/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/shadcn/ui/tooltip";
import { usePermissions } from "~/hooks/usePermissions";
import { useI18n } from "~/lib/i18n/useI18n";
import { isArray } from "~/lib/lodash";
import { useFetchCampaignsQuery } from "~/pages/api/compensation-review/campaigns/fetch-campaigns";

export const SidebarCompensationReviews: React.FC = () => {
  const { t } = useI18n();
  const router = useRouter();
  const { permissions } = usePermissions();
  const { fullOpen, isMobile } = useSidebar();

  const { data: campaigns, isFetching: isFetchingCampaigns } = useFetchCampaignsQuery({
    input: { onlyActive: true },
    options: {
      enabled: permissions.canAccessCompensationReview,
      staleTime: Infinity,
    },
  });

  if (!permissions.canAccessCompensationReview) return null;

  const showCampaigns = isArray(campaigns) && campaigns.length > 0;
  const showAllCampaigns = true;

  const showSection = showCampaigns || showAllCampaigns;

  if (!showSection) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t("components.layout.sidebar.compensation-reviews")}</SidebarGroupLabel>
      <SidebarMenu>
        {showAllCampaigns && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.compensation-review-campaigns")}
              isActive={router.pathname === "/compensation-review"}
            >
              <Link to={{ pathname: "/compensation-review" }} noLinkStyle>
                <CompensationReview className="-ml-px !size-[18px]" />
                <span>{t("components.layout.sidebar.campaigns")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isFetchingCampaigns && <SidebarMenuSkeleton className="group-data-[collapsible=icon]:hidden" />}

        {showCampaigns && (
          <>
            {fullOpen &&
              campaigns.map((campaign) => {
                const isActive =
                  router.pathname.startsWith(`/compensation-review/campaigns/[campaignId]`) &&
                  router.query.campaignId === campaign.id.toString();

                return (
                  <SidebarMenuItem key={campaign.id} className="group-data-[collapsible=icon]:hidden">
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link
                        to={{
                          pathname: "/compensation-review/campaigns/[campaignId]",
                          query: { campaignId: campaign.id.toString() },
                        }}
                        noLinkStyle
                      >
                        <DescriptionOutlined />
                        <span>{campaign.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

            {!fullOpen && (
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger>
                          <DropdownMenuTrigger>
                            <SidebarMenuButton>
                              <DescriptionOutlined />
                              <span>{t("components.layout.sidebar.campaigns")}</span>
                            </SidebarMenuButton>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="right">{t("components.layout.sidebar.campaigns")}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <DropdownMenuContent
                      className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                      side={isMobile ? "bottom" : "right"}
                      align="end"
                      sideOffset={4}
                    >
                      <DropdownMenuLabel>{t("components.layout.sidebar.campaigns")}</DropdownMenuLabel>

                      {campaigns.map((campaign) => (
                        <DropdownMenuItem key={campaign.id}>
                          <Link
                            to={{
                              pathname: "/compensation-review/campaigns/[campaignId]",
                              query: { campaignId: campaign.id.toString() },
                            }}
                            noLinkStyle
                          >
                            {campaign.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            )}
          </>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
};
